import React, { useState, useEffect } from "react";
import {
  Sun,
  Moon,
  Monitor,
  Trash2,
  Copy,
  ChevronRight,
  ChevronLeft,
  X,
  User,
  Check,
  ChevronDown,
  Settings as SettingsIcon,
  Database,
  FileText,
  Edit,
  ExternalLink,
  Link as LinkIcon,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Tablet,
  Smartphone,
  Mic,
  Sparkles,
  CalendarDays,
  GripVertical,
  Plus,
  LayoutGrid,
  LogOut,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useTheme } from "../contexts/ThemeContext";
import { storage, StorageManager } from "@/utils/storageManager";
import { toast } from "@/components/ui/use-toast";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSupabase } from "@/hooks/useSupabase";
import { useIsDarkMode } from "@/hooks/useIsDarkMode";
import { cn } from "@/lib/utils";
import { PricingModal } from "@/components/PricingModal";
import { getDailyQuotaStatus } from '@/services/dailyQuotaService';
import { FeedbackModal } from "@/components/FeedbackModal";
import { ReportBugModal } from "@/components/ReportBugModal";
import { LOCAL_STORAGE_KEYS } from "@/utils/storageKeys";
import { getUserFacts, setUserFacts } from "@/services/userFactsService";
import Cropper from 'react-easy-crop';
import { getCroppedImg } from "@/utils/cropImage";
import { useChatStorage } from "@/hooks/useChatStorage";

interface SettingsPageProps {
  user?: {
    name?: string;
    email?: string;
    createdAt?: string;
    isPremium: boolean;
  };
  onLogout?: () => void;
}
const TAB_OPTIONS = ["general", "profile", "data", "personalization", "apps", "about"] as const;
type SettingsTab = (typeof TAB_OPTIONS)[number];

type MemoryKeyFact = {
  id: string;
  text: string;
};

// Add this interface
interface QuickTaskResponse {
  success: boolean;
  result?: string;
  error?: string;
}

interface SortableMemoryItemProps {
  id: string;
  value: string;
  onChange: (id: string, newVal: string) => void;
  onRemove: (id: string) => void;
  isDarkMode: boolean;
}

function SortableMemoryItem({ id, value, onChange, onRemove, isDarkMode }: SortableMemoryItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : "auto",
    position: "relative" as const,
  };

  const MAX_CHARS = 300;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "mb-2 flex flex-col gap-1 group transition-opacity",
        isDragging ? "opacity-50" : "opacity-100"
      )}
    >
      <div className="flex items-center gap-2">
        <div
          {...attributes}
          {...listeners}
          className={cn(
            "flex-shrink-0 cursor-grab active:cursor-grabbing transition-colors touch-none select-none",
            "text-zinc-500 hover:text-zinc-300"
          )}
        >
          <GripVertical size={20} />
        </div>

        <input
          value={value}
          onChange={(e) => {
            if (e.target.value.length <= MAX_CHARS) {
              onChange(id, e.target.value);
            }
          }}
          maxLength={MAX_CHARS}
          placeholder="Enter a key fact..."
          className={cn(
            "flex w-full h-12 rounded-full border px-4 py-2 text-sm transition-all",
            "focus-visible:outline-none focus-visible:ring-1",
            isDarkMode
              ? "border-zinc-800 bg-zinc-900 text-zinc-100 placeholder:text-zinc-500 focus-visible:ring-zinc-600 focus-visible:border-zinc-600"
              : "border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus-visible:ring-gray-300 focus-visible:border-gray-300"
          )}
        />

        <button
          type="button"
          onClick={() => onRemove(id)}
          className={cn(
            "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors",
            isDarkMode
              ? "text-zinc-500 hover:bg-red-900/20 hover:text-red-500"
              : "text-gray-500 hover:bg-red-50 hover:text-red-600"
          )}
          aria-label="Remove"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Character counter */}
      <div className={cn(
        "text-[10px] text-right pr-12 select-none",
        isDarkMode ? "text-zinc-500" : "text-gray-400",
        value.length >= MAX_CHARS && "text-red-500"
      )}>
        {value.length}/{MAX_CHARS}
      </div>
    </div>
  );
}

function Settings({ user, onLogout }: SettingsPageProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, signOut } = useSupabase();
  const { theme, setTheme } = useTheme();
  const isDarkMode = useIsDarkMode();
  const [activeTab, setActiveTab] = useState<SettingsTab>("general");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // removed confirm flow to avoid warnings and simplify UX
  const [reportIssue, setReportIssue] = useState("");
  const [name, setName] = useState(user?.name || "");
  const isSmDevice = useIsMobile();
  const [gender, setGender] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [improveModel, setImproveModel] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("improveModel") === "true";
    }
    return false;
  });
  const [sharedLinks, setSharedLinks] = useState<any[]>([]);
  const [isLoadingSharedLinks, setIsLoadingSharedLinks] = useState(false);
  const [showSharedLinks, setShowSharedLinks] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState("");
  const [reportDescription, setReportDescription] = useState("");

  // Cropping state
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const cropRafIdRef = React.useRef<number | null>(null);
  const pendingCropRef = React.useRef<{ x: number; y: number } | null>(null);
  const pendingZoomRef = React.useRef<number | null>(null);

  const flushCropperUpdates = React.useCallback(() => {
    cropRafIdRef.current = null;

    if (pendingCropRef.current) {
      setCrop(pendingCropRef.current);
      pendingCropRef.current = null;
    }

    if (pendingZoomRef.current !== null) {
      setZoom(pendingZoomRef.current);
      pendingZoomRef.current = null;
    }
  }, []);

  const scheduleCropperUpdate = React.useCallback(() => {
    if (cropRafIdRef.current !== null) return;
    cropRafIdRef.current = window.requestAnimationFrame(flushCropperUpdates);
  }, [flushCropperUpdates]);

  const handleCropChange = React.useCallback(
    (nextCrop: { x: number; y: number }) => {
      pendingCropRef.current = nextCrop;
      scheduleCropperUpdate();
    },
    [scheduleCropperUpdate]
  );

  const handleZoomChange = React.useCallback(
    (nextZoom: number) => {
      pendingZoomRef.current = nextZoom;
      scheduleCropperUpdate();
    },
    [scheduleCropperUpdate]
  );

  useEffect(() => {
    return () => {
      if (cropRafIdRef.current !== null) {
        window.cancelAnimationFrame(cropRafIdRef.current);
      }
    };
  }, []);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [isReportSubmitting, setIsReportSubmitting] = useState(false);
  const [isReportSubmitted, setIsReportSubmitted] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [creditsRemaining, setCreditsRemaining] = useState<number>(200);
  const [dailyCreditsRemaining, setDailyCreditsRemaining] = useState<number>(300);
  const [planTier, setPlanTier] = useState<string>('free');

  const [memoryItems, setMemoryItems] = useState<MemoryKeyFact[]>([
    { id: "1", text: "" },
  ]);
  const MAX_MEMORY_ITEMS = 5;

  const memorySensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleMemoryDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id !== over.id) {
      setMemoryItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const addMemoryItem = () => {
    if (memoryItems.length < MAX_MEMORY_ITEMS) {
      setMemoryItems([...memoryItems, { id: crypto.randomUUID(), text: "" }]);
    }
  };

  const removeMemoryItem = (id: string) => {
    setMemoryItems(memoryItems.filter((item) => item.id !== id));
  };

  const updateMemoryItemText = (id: string, text: string) => {
    setMemoryItems(memoryItems.map(item => item.id === id ? { ...item, text } : item));
  };

  const [isSavingMemory, setIsSavingMemory] = useState(false);
  const [userFactsSummary, setUserFactsSummary] = useState("");
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [updateCount, setUpdateCount] = useState(0);
  const [maxUpdates, setMaxUpdates] = useState(2);

  // Load existing user facts on mount
  useEffect(() => {
    const loadFacts = async () => {
      if (!authUser) return;
      try {
        const { facts, summary, updateCount: count, maxUpdates: max } = await getUserFacts();
        if (facts && facts.length > 0) {
          setMemoryItems(facts.map((text, idx) => ({ id: `loaded-${idx}`, text })));
        }
        if (summary) {
          setUserFactsSummary(summary);
        }
        setUpdateCount(count);
        setMaxUpdates(max);
      } catch (err) {
        console.error("Failed to load user facts:", err);
      }
    };
    loadFacts();
  }, [authUser]);

  const handleSaveMemory = async () => {
    if (!authUser) {
      toast({
        title: "Sign in required",
        description: "Please sign in to save your memories.",
        variant: "destructive",
      });
      return;
    }

    // 0. Strict Fresh Check of Limit
    // We fetch the count again to ensure no race conditions or stale state
    try {
      const { updateCount: freshCount } = await getUserFacts();
      setUpdateCount(freshCount);

      if (freshCount >= maxUpdates) {
        toast({
          title: "Limit Reached",
          description: `You can only update your key facts ${maxUpdates} times per day.`,
          variant: "destructive",
        });
        return;
      }
    } catch (error) {
      console.error("Failed to verify update count:", error);
      // We'll proceed but the server might reject it anyway
    }

    if (updateCount >= maxUpdates) {
      toast({
        title: "Limit Reached",
        description: `You can only update your key facts ${maxUpdates} times per day.`,
        variant: "destructive",
      });
      return;
    }

    const factsToSummarize = memoryItems
      .map((item) => item.text.trim())
      .filter((text) => text.length > 0);

    if (factsToSummarize.length === 0) {
      // If clearing, maybe allow? But for now let's require facts.
      toast({ title: "No facts", description: "Add facts first.", variant: "destructive" });
      return;
    }

    setIsSavingMemory(true);
    try {
      // 1. Generate Summary
      const { data: summaryData, error: summaryError } = await supabase.functions.invoke("quickTask", {
        body: {
          prompt: factsToSummarize.join("\n"),
          mode: 'summarize_facts',
          existingSummary: userFactsSummary // Pass existing summary so it can be updated
        },
      });

      if (summaryError || !summaryData?.success) {
        throw new Error(summaryData?.error || "Failed to generate summary");
      }

      const newSummary = summaryData.result;
      setUserFactsSummary(newSummary);

      // 2. Save Summary (and check limit)
      // Pass empty array for facts since we aren't saving them to DB anymore
      await setUserFacts([], newSummary);

      setUpdateCount(prev => prev + 1);

      toast({
        title: "Memory updated",
        description: "",
      });
    } catch (err: any) {
      console.error("Failed to save memory:", err);
      // Check for rate limit message (or handle generically)
      const errorMsg = err.message || "";
      const isRateLimit = errorMsg.includes("Daily limit") ||
        errorMsg.includes("You can only update your key facts twice per day") ||
        err.body?.includes("Daily limit");

      toast({
        title: isRateLimit ? "Limit Reached" : "Error",
        description: isRateLimit
          ? `You can only update your key facts ${maxUpdates} times per day.`
          : (err?.message || "Failed to save memory. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsSavingMemory(false);
    }
  };
  const [storageUsage, setStorageUsage] = useState({
    used: 0,
    total: 5,
    percentage: 0,
  });

  const handleSummarize = async () => {
    const factsToSummarize = memoryItems
      .map((item) => item.text.trim())
      .filter((text) => text.length > 0);

    if (factsToSummarize.length === 0) {
      toast({
        title: "No facts to summarize",
        description: "Please add some key facts first.",
        variant: "destructive",
      });
      return;
    }

    // 0. Strict Fresh Check of Limit
    try {
      const { updateCount: freshCount } = await getUserFacts();
      setUpdateCount(freshCount);

      if (freshCount >= maxUpdates) {
        toast({
          title: "Limit Reached",
          description: `You can only update your key facts ${maxUpdates} times per day.`,
          variant: "destructive",
        });
        return;
      }
    } catch (error) {
      console.error("Failed to verify update count:", error);
    }

    if (updateCount >= maxUpdates) {
      toast({
        title: "Limit Reached",
        description: `You can only update your key facts ${maxUpdates} times per day.`,
        variant: "destructive",
      });
      return;
    }

    setIsSummarizing(true);
    try {
      const { data, error } = await supabase.functions.invoke("quickTask", {
        body: {
          prompt: factsToSummarize.join("\n"),
          mode: 'summarize_facts'
        },
      });

      if (error || !data?.success) throw new Error(data?.error || error?.message || "Failed to summarize");

      setUserFactsSummary(data.result);
      toast({
        title: "Summary generated",
        description: "Don't forget to save your changes.",
      });
    } catch (err: any) {
      console.error("Summarization failed:", err);
      toast({
        title: "Error",
        description: err?.message || "Failed to generate summary.",
        variant: "destructive",
      });
    } finally {
      setIsSummarizing(false);
    }
  };
  const [isClearingChats, setIsClearingChats] = useState(false);
  const [backgroundType, setBackgroundType] = useState<
    "solid" | "image"
  >((storage.getAppSettings()?.backgroundType as 'solid' | 'image' | undefined) ?? "solid");
  const [isSaving, setIsSaving] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showVoiceMenu, setShowVoiceMenu] = useState(false);
  const [language, setLanguage] = useState("System");

  const VOICE_MODE_CARDS_SESSION_KEY = "voice_mode_cards_v1";
  const [hasVoiceSavedMessages, setHasVoiceSavedMessages] = useState(false);
  const [hasVoiceSavedAudio, setHasVoiceSavedAudio] = useState(false);

  const VOICE_MODEL_STORAGE_KEY = "voice_mode_voice_model_v1";
  const [selectedVoiceModel, setSelectedVoiceModel] = useState<string>(() => {
    try {
      return localStorage.getItem(VOICE_MODEL_STORAGE_KEY) || "aura-2-aries-en";
    } catch {
      return "aura-2-aries-en";
    }
  });

  const refreshVoiceSessionFlags = React.useCallback(() => {
    try {
      const raw = sessionStorage.getItem(VOICE_MODE_CARDS_SESSION_KEY);
      if (!raw) {
        setHasVoiceSavedMessages(false);
        setHasVoiceSavedAudio(false);
        return;
      }

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        setHasVoiceSavedMessages(false);
        setHasVoiceSavedAudio(false);
        return;
      }

      const hasMessages = parsed.length > 0;
      const hasAudio = parsed.some((c: any) => Boolean(c?.aiAudioBlob));
      setHasVoiceSavedMessages(hasMessages);
      setHasVoiceSavedAudio(hasAudio);
    } catch {
      setHasVoiceSavedMessages(false);
      setHasVoiceSavedAudio(false);
    }
  }, []);

  useEffect(() => {
    refreshVoiceSessionFlags();
  }, [refreshVoiceSessionFlags]);

  const VOICES = [
    { id: "aura-2-aries-en", title: "Aries", subtitle: "Bold and direct" },
    { id: "aura-2-pluto-en", title: "Pluto", subtitle: "Calm and steady" },
    { id: "aura-2-selene-en", title: "Selene", subtitle: "Bright and friendly" },
    { id: "aura-2-vesta-en", title: "Vesta", subtitle: "Warm and confident" },
  ];

  const [appsChatSearch, setAppsChatSearch] = useState("");
  const [appsSessions, setAppsSessions] = useState<any[]>([]);

  const chatStorage = useChatStorage(authUser || null);
  const storageManager = React.useMemo(() => {
    try {
      return StorageManager.getInstance();
    } catch {
      return null as any;
    }
  }, []);

  useEffect(() => {
    const loadSessions = async () => {
      if (authUser && chatStorage.sessions) {
        const sorted = [...chatStorage.sessions].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
        setAppsSessions(sorted);
      } else if (storageManager) {
        try {
          const all = storageManager.getAllSessions ? storageManager.getAllSessions() : [];
          const sorted = all.sort((a: any, b: any) => new Date(b.updatedAt || b.timestamp).getTime() - new Date(a.updatedAt || a.timestamp).getTime());
          setAppsSessions(sorted);
        } catch {
          setAppsSessions([]);
        }
      }
    };

    loadSessions();
  }, [authUser, chatStorage.sessions, storageManager]);

  const filteredAppsSessions = React.useMemo(() => {
    const term = appsChatSearch.trim().toLowerCase();
    if (!term) return appsSessions;
    return appsSessions.filter((s) => {
      const title = (s.title || s.name || "Untitled Chat").toString().toLowerCase();
      return title.includes(term);
    });
  }, [appsChatSearch, appsSessions]);

  const groupedAppsSessions = React.useMemo(() => {
    const groups: { label: string; items: any[] }[] = [];
    const now = new Date();

    const getLabel = (d: Date) => {
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const thatDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const diffDays = Math.round((today.getTime() - thatDay.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return "Today";
      if (diffDays === 1) return "Yesterday";
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const map: Record<string, any[]> = {};
    for (const s of filteredAppsSessions) {
      const rawDate = (s.updated_at || s.updatedAt || s.timestamp || s.created_at) as string;
      const date = new Date(rawDate);
      const label = getLabel(date);
      if (!map[label]) map[label] = [];
      map[label].push(s);
    }

    Object.entries(map).forEach(([label, items]) => {
      groups.push({ label, items });
    });

    return groups;
  }, [filteredAppsSessions]);

  const handleVoiceChange = (voiceId: string) => {
    setSelectedVoiceModel(voiceId);
    try {
      localStorage.setItem(VOICE_MODEL_STORAGE_KEY, voiceId);
    } catch { }
  };

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { tabId } = useParams<{ tabId?: string }>();

  const fetchProfileData = async () => {
    if (!authUser) return;
    try {
      const { data, error } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("user_id", authUser.id)
        .maybeSingle(); // handle 0 rows gracefully

      if (error && error.code !== "PGRST116") throw error;

      if (data) {
        setProfileData(data);
        if (data.full_name) setName(data.full_name);
        if (data.avatar_url) setAvatarUrl(data.avatar_url);
        if (data.gender) setGender(data.gender);
      }
    } catch (err) {
      console.error("Error fetching profile data:", err);
    }
  };

  useEffect(() => {
    if (authUser) {
      fetchProfileData();
    }
  }, [authUser]);

  const handleTabChange = (tab: SettingsTab) => {
    if (tab === activeTab) {
      return;
    }

    setActiveTab(tab);
    // Replace history entry when switching tabs so closing Settings goes back to the previous app page
    navigate(`/settings/${tab}`, { replace: true });
  };



  useEffect(() => {
    const normalizedTab = (tabId || "").toLowerCase();

    if (!tabId) {
      navigate("/settings/general", { replace: true });
      return;
    }

    if (TAB_OPTIONS.includes(normalizedTab as SettingsTab)) {
      setActiveTab(normalizedTab as SettingsTab);
    } else {
      navigate("/settings/general", { replace: true });
    }
  }, [navigate, tabId]);

  const reportOptions = [
    {
      id: "bug",
      title: "Bug",
      description: "Something is not working as expected",
    },
    {
      id: "feature",
      title: "Feature Request",
      description: "Suggest a new feature",
    },
    {
      id: "ui",
      title: "UI/UX Issue",
      description: "Problem with the interface",
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await storage.updateAppSettings({
        backgroundType,
      });

      if (name !== user?.name) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      toast({
        title: "Settings saved",
        description: "Your preferences have been updated successfully.",
      });
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast({
        title: "Error",
        description: "Failed to save settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      toast({
        title: "Signed out",
        description: "You have been successfully signed out.",
      });
      navigate("/");
    } catch (err: any) {
      console.error("Sign out error:", err);
      toast({
        title: "Error",
        description: err?.message || "Failed to sign out.",
        variant: "destructive",
      });
    }
  };

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  useEffect(() => {
    setStorageUsage(storage.getStorageUsage());
  }, []);

  const handleBackgroundChange = async (
    type: "solid" | "image"
  ) => {
    setBackgroundType(type);

    try {
      await storage.updateAppSettings({ backgroundType: type });

      window.dispatchEvent(
        new CustomEvent("appSettingsChanged", {
          detail: { backgroundType: type },
        })
      );
    } catch (error) {
      console.error("Failed to update background setting:", error);
      setBackgroundType(backgroundType);

      toast({
        title: "Error",
        description: "Failed to update background setting.",
        variant: "destructive",
      });
    }
  };

  const handleClearChats = async () => {
    setShowDeleteConfirm(false);
    setIsClearingChats(true);

    try {
      await storage.clearChats();
      setStorageUsage(storage.getStorageUsage());

      toast({
        title: "Chats cleared",
        description: "All chat history has been deleted.",
        variant: "default",
      });
    } catch (error) {
      console.error("Failed to clear chats:", error);
      toast({
        title: "Error",
        description: "Failed to clear chat history. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsClearingChats(false);
    }
  };

  const handleReportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reportType) return;

    setIsReportSubmitting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setIsReportSubmitted(true);
      setTimeout(() => {
        setShowReportModal(false);
        setIsReportSubmitted(false);
        setReportType("");
        setReportDescription("");
      }, 1000);
    } finally {
      setIsReportSubmitting(false);
    }
  };

  const fetchSharedLinks = async () => {
    if (!authUser) return;
    setIsLoadingSharedLinks(true);
    try {
      const { data, error } = await supabase
        .from("chat_sessions")
        .select("id, title, created_at, is_shared")
        .eq("user_id", authUser.id)
        .eq("is_shared", true);

      if (error) throw error;
      setSharedLinks(data || []);
    } catch (err) {
      console.error("Error fetching shared links:", err);
    } finally {
      setIsLoadingSharedLinks(false);
    }
  };

  useEffect(() => {
    if (showSharedLinks) {
      fetchSharedLinks();
    }
  }, [showSharedLinks]);

  useEffect(() => {
    const safeParseInt = (v: string | null, fallback: number) => {
      const n = parseInt(String(v ?? ''), 10);
      return Number.isFinite(n) ? n : fallback;
    };

    const getNextDailyReset = () => {
      const now = new Date();
      const reset = new Date(now);
      reset.setHours(0, 30, 0, 0);
      if (now.getTime() >= reset.getTime()) {
        reset.setDate(reset.getDate() + 1);
      }
      return reset;
    };

    const load = async () => {
      try {
        if (authUser) {
          const status = await getDailyQuotaStatus();
          setCreditsRemaining(status?.remaining?.messages ?? 0);
          setDailyCreditsRemaining(status?.limits?.messages ?? 0);
          setPlanTier((status?.tier || 'free').toString());
          return;
        }

        const storedCredits =
          localStorage.getItem(LOCAL_STORAGE_KEYS.creditsRemaining) ||
          localStorage.getItem('creditsRemaining');
        const nextCredits = storedCredits == null ? 200 : safeParseInt(storedCredits, 200);
        if (storedCredits == null) {
          localStorage.setItem(LOCAL_STORAGE_KEYS.creditsRemaining, String(nextCredits));
        }
        setCreditsRemaining(nextCredits);

        const resetAtRaw =
          localStorage.getItem(LOCAL_STORAGE_KEYS.dailyCreditsResetAt) ||
          localStorage.getItem('dailyCreditsResetAt');
        const resetAtMs = safeParseInt(resetAtRaw, 0);
        const nowMs = Date.now();
        const shouldReset = !resetAtMs || nowMs >= resetAtMs;
        if (shouldReset) {
          const nextReset = getNextDailyReset();
          localStorage.setItem(LOCAL_STORAGE_KEYS.dailyCreditsRemaining, '300');
          localStorage.setItem(LOCAL_STORAGE_KEYS.dailyCreditsResetAt, String(nextReset.getTime()));
          setDailyCreditsRemaining(300);
        } else {
          const storedDaily =
            localStorage.getItem(LOCAL_STORAGE_KEYS.dailyCreditsRemaining) ||
            localStorage.getItem('dailyCreditsRemaining');
          const nextDaily = storedDaily == null ? 300 : safeParseInt(storedDaily, 300);
          if (storedDaily == null) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.dailyCreditsRemaining, String(nextDaily));
          }
          setDailyCreditsRemaining(nextDaily);
        }
      } catch {
        setCreditsRemaining(200);
        setDailyCreditsRemaining(300);
        setPlanTier('free');
      }
    };

    void load();
  }, [authUser]);

  const handleDeleteSharedLink = async (chatId: string) => {
    try {
      const { error } = await supabase
        .from("chat_sessions")
        .update({ is_shared: false })
        .eq("id", chatId);

      if (error) throw error;
      setSharedLinks(prev => prev.filter(link => link.id !== chatId));
      toast({
        title: "Link deleted",
        description: "The shared link has been removed.",
      });
    } catch (err) {
      console.error("Error deleting shared link:", err);
      toast({
        title: "Error",
        description: "Failed to delete shared link.",
        variant: "destructive"
      });
    }
  };

  const isSmallDeviceValue = isSmDevice;

  const ProfileEditForm = () => (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <Label htmlFor="edit-name">Name</Label>
        <Input
          id="edit-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your full name"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="edit-avatar">Avatar URL</Label>
        <Input
          id="edit-avatar"
          value={avatarUrl}
          onChange={(e) => setAvatarUrl(e.target.value)}
          placeholder="https://example.com/avatar.png"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="edit-gender">Gender</Label>
        <select
          id="edit-gender"
          value={gender}
          onChange={(e) => setGender(e.target.value)}
          className="w-full flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <option value="">Select Gender</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
          <option value="prefer-not-to-say">Prefer not to say</option>
        </select>
        <p className="text-[10px] text-muted-foreground italic">Gender can only be set once.</p>
      </div>
      <Button
        onClick={async () => {
          setIsSaving(true);
          try {
            const upData: any = { full_name: name, avatar_url: avatarUrl };
            if (gender) upData.gender = gender;

            const { error, data } = await supabase
              .from("user_profiles")
              .update(upData)
              .eq("user_id", authUser?.id)
              .select()
              .single();

            if (error) throw error;

            if (data) {
              setProfileData(data);
              if (data.full_name) setName(data.full_name);
              if (data.avatar_url) setAvatarUrl(data.avatar_url);
            }

            toast({ title: "Profile updated", description: "Your profile has been saved successfully." });
          } catch (err: any) {
            console.error("Profile update error:", err);
            toast({
              title: "Error",
              description: err.message || "Failed to update profile.",
              variant: "destructive"
            });
          } finally {
            setIsSaving(false);
          }
        }}
        className="w-full mt-4"
        disabled={isSaving}
      >
        {isSaving ? "Saving..." : "Save Profile"}
      </Button>
    </div>
  );

  return (
    <div className="relative min-h-screen bg-[#0a0a0a] overflow-x-hidden overflow-y-auto">
      <div className={`min-h-screen ${isDarkMode ? "bg-[#0a0a0a]" : "bg-gray-50"} text-white flex items-center justify-center`}>
        <div className={`w-full max-w-4xl h-[100vh] md:mx-2 md:h-[95vh] ${isDarkMode ? "bg-[#121212] border-white/5" : "bg-white border-gray-200"} md:rounded-3xl border shadow-2xl overflow-hidden flex flex-col`}>
          {/* Header */}
          <div className={`flex items-center justify-between py-3 px-6 border-b ${isDarkMode ? "border-white/5" : "border-gray-100"}`}>
            <h1 className={`text-lg font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Settings</h1>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                // Prefer going back to the previous route; if not possible, fallback to home
                try {
                  if (window.history && window.history.length > 1) {
                    navigate(-1);
                  } else if ((location as any)?.state?.from) {
                    navigate((location as any).state.from);
                  } else {
                    navigate("/");
                  }
                } catch {
                  navigate("/");
                }
              }}
              className={`h-8 w-8 rounded-xl ${isDarkMode ? "text-gray-400 hover:text-white hover:bg-white/5" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Main Content Area */}
          <div className="flex flex-1 overflow-auto transition-all duration-200 ease-in-out">
            <div className="flex flex-col md:flex-row w-full h-full transition-all duration-200 ease-in-out">
              {/* Sidebar Navigation (desktop only) */}
              <div className={`hidden p-3 transition-all duration-200 ease-in-out md:block md:w-64 flex-shrink-0 
                ${isDarkMode ? "bg-transparent border-r border-white/5" : "bg-gray-50/50 border-r border-gray-100"}`}>
                <nav className="space-y-1 my-4">
                  {[{
                    id: "general", label: "General", icon: SettingsIcon
                  },
                  { id: "profile", label: "Profile", icon: User },
                  { id: "data", label: "Data", icon: Database },
                  { id: "personalization", label: "Personalization", icon: Sparkles },
                  { id: "apps", label: "Apps", icon: LayoutGrid },
                  { id: "about", label: "About", icon: FileText },
                  ].map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => handleTabChange(id as SettingsTab)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${activeTab === id
                        ? isDarkMode ? "bg-[#303030] text-white" : "bg-white border border-gray-200 shadow-sm text-gray-900"
                        : isDarkMode ? "text-gray-400 hover:bg-white/5 hover:text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                        }`}
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Main Content Area */}
              <div className="flex-1 bg-transparent rounded-2xl">
                <div className="py-3 px-1 sm:px-6 h-full overflow-auto">
                  {activeTab === "general" && (
                    <div className="space-y-8 pt-2 px-3 animate-in fade-in slide-in-from-bottom-2 h-[max-content] pb-32 lg:pb-0">
                      <div className="space-y-4">
                        <div className={`flex items-center justify-between p-4 rounded-2xl bg-transparent`}>
                          <div className="flex items-center gap-3">
                            <Sun className={`h-5 w-5 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`} />
                            <span className={`text-sm font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Appearance</span>
                          </div>
                          <div className="relative">
                            <Button
                              variant="ghost"
                              onClick={() => setShowThemeMenu(!showThemeMenu)}
                              className={cn(
                                "flex items-center gap-2 px-3 py-1.5 h-auto rounded-xl transition-all border border-transparent hover:border-white/10",
                                isDarkMode ? "text-white/90 hover:bg-white/5" : "text-gray-900 hover:bg-gray-100"
                              )}
                            >
                              <span className="text-sm border-0 capitalize">{theme}</span>
                              <ChevronDown className="h-4 w-4 text-gray-500" />
                            </Button>

                            {showThemeMenu && (
                              <>
                                <div
                                  className="fixed inset-0 z-40"
                                  onClick={() => setShowThemeMenu(false)}
                                />
                                <div
                                  className={cn(
                                    "absolute right-0 mt-2 w-40 rounded-2xl p-1.5 shadow-xl border z-50 animate-in fade-in zoom-in-95 space-y-1 duration-200",
                                    isDarkMode ? "bg-[#252525] border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"
                                  )}
                                >
                                  {[
                                    {
                                      id: "system",
                                      label: "System",
                                      icon: () => (
                                        <>
                                          <Monitor className="hidden md:block h- w-4" />
                                          <Tablet className="hidden sm:block md:hidden h-4 w-4" />
                                          <Smartphone className="sm:hidden h-4 w-4" />
                                        </>
                                      )
                                    },
                                    { id: "dark", label: "Dark", icon: Moon },
                                    { id: "light", label: "Light", icon: Sun },
                                  ].map((opt) => (
                                    <button
                                      key={opt.id}
                                      onClick={() => {
                                        setTheme(opt.id as any);
                                        setShowThemeMenu(false);
                                      }}
                                      className={cn(
                                        "w-full flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition-colors outline-none",
                                        theme === opt.id
                                          ? (isDarkMode ? "bg-white/10" : "bg-gray-100")
                                          : "hover:bg-white/5"
                                      )}
                                    >
                                      <div className="flex items-center gap-3">
                                        <opt.icon className="h-4 w-4" />
                                        <span className="text-sm font-medium">{opt.label}</span>
                                      </div>
                                      {theme === opt.id && <Check className="h-4 w-4" />}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <h3 className={`text-sm font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Background</h3>
                        <div className="flex flex-row flex-wrap justify-left gap-4">
                          <div className="flex flex-col flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => handleBackgroundChange("solid")}
                              className={cn(
                                "group relative h-[104px] w-[164px] rounded-3xl border overflow-hidden transition-all outline-none",
                                backgroundType === "solid"
                                  ? "border-[#4D8DFF] ring-2 ring-[#4D8DFF]/35"
                                  : (isDarkMode ? "border-white/10" : "border-gray-200"),
                                isDarkMode ? "bg-[#1a1a1a]" : "bg-[#F3F3F3]"
                              )}
                              aria-pressed={backgroundType === "solid"}
                            >
                              {backgroundType === "solid" && (
                                <div className="absolute left-3 top-3 h-6 w-6 rounded-full bg-[#4D8DFF] text-white flex items-center justify-center">
                                  <Check className="h-4 w-4" />
                                </div>
                              )}

                              {/* Mini Chat Preview Container */}
                              <div className="absolute inset-0 px-3 pt-3 pb-3">
                                <div className="flex h-full flex-col justify-between gap-1">
                                  {/* User Message Bubble (top-right) */}
                                  <div className="flex justify-end">
                                    <div className={cn(
                                      "h-5 w-[82px] rounded-md border",
                                      isDarkMode ? "bg-black/25 border-white/10" : "bg-white/60 border-black/10"
                                    )} />
                                  </div>

                                  {/* AI Response Bubble (mid-left) */}
                                  <div className="flex justify-start">
                                    <div className={cn(
                                      "h-7 w-[124px] rounded-md border",
                                      isDarkMode ? "bg-white/8 border-white/10" : "bg-white/60 border-black/10"
                                    )} />
                                  </div>

                                  {/* Input Bar (bottom) */}
                                  <div className={cn(
                                    "relative h-6 rounded-md border",
                                    isDarkMode ? "bg-white/6 border-white/10" : "bg-white/70 border-black/10"
                                  )}>
                                    {/* Input placeholder line */}
                                    <div className={cn(
                                      "absolute left-3 top-1/2 -translate-y-1/2 h-2 w-[86px] rounded-md",
                                      isDarkMode ? "bg-white/20" : "bg-black/10"
                                    )} />
                                    {/* Action buttons (right side of input) */}
                                    <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                                      <div className={cn(
                                        "h-3 w-3 rounded-full",
                                        isDarkMode ? "bg-white/10 border-white/15" : "bg-black/5 border-black/10"
                                      )} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </button>

                            <div className={cn(
                              "text-sm text-center font-medium",
                              backgroundType === "solid"
                                ? (isDarkMode ? "text-white" : "text-gray-900")
                                : (isDarkMode ? "text-white/70" : "text-gray-600")
                            )}>
                              Default
                            </div>
                          </div>

                          {/* <div className="flex flex-col gap-2">
                            <button
                              type="button"
                              onClick={() => handleBackgroundChange("image")}
                              className={cn(
                                "group relative h-[104px] w-[164px] rounded-3xl border overflow-hidden transition-all outline-none",
                                backgroundType === "image"
                                  ? "border-[#4D8DFF] ring-2 ring-[#4D8DFF]/35"
                                  : (isDarkMode ? "border-white/10" : "border-gray-200")
                              )}
                              style={{
                                backgroundImage: `url(${authBg})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                              }}
                              aria-pressed={backgroundType === "image"}
                            >
                              {backgroundType === "image" && (
                                <div className="absolute left-3 top-3 h-6 w-6 rounded-full bg-[#4D8DFF] text-white flex items-center justify-center">
                                  <Check className="h-4 w-4" />
                                </div>
                              )}

                              
                              <div className="absolute inset-0 px-3 pt-3 pb-3">
                                <div className="flex h-full flex-col justify-between gap-1">
                                  
                                  <div className="flex justify-end">
                                    <div className={cn(
                                      "h-5 w-[82px] rounded-md border",
                                      isDarkMode ? "bg-[#1C1C1C90] border-white/20" : "bg-white/60 border-black/10"
                                    )} />
                                  </div>

                                  
                                  <div className="flex justify-start">
                                    <div className={cn(
                                      "h-7 w-[124px] rounded-md border",
                                      isDarkMode ? "bg-[#1C1C1C90] border-white/20" : "bg-white/60 border-black/10"
                                    )} />
                                  </div>

                                  
                                  <div className={cn(
                                    "relative h-6 rounded-md border",
                                    isDarkMode ? "bg-[#1C1C1C90] border-white/20" : "bg-white/70 border-black/10"
                                  )}>
                                    
                                    <div className={cn(
                                      "absolute left-3 top-1/2 -translate-y-1/2 h-2 w-[86px] rounded-md",
                                      isDarkMode ? "bg-[#1c1c1c80]" : "bg-black/10"
                                    )} />
                                    
                                    <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                                      <div className={cn(
                                        "h-3 w-3 rounded-full",
                                        isDarkMode ? "bg-[#1c1c1c90] border-white/15" : "bg-black/5 border-black/10"
                                      )} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </button>

                            <div className={cn(
                              "text-sm font-medium",
                              backgroundType === "image"
                                ? (isDarkMode ? "text-white" : "text-gray-900")
                                : (isDarkMode ? "text-white/70" : "text-gray-600")
                            )}>
                              Image
                            </div>
                          </div> */}
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <label className={`text-sm font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Language</label>
                        <div className="flex gap-2">
                          {["English"].map((lang) => (
                            <button
                              key={lang}
                              onClick={() => setLanguage(lang)}
                              className={`px-4 py-1.5 rounded-full border transition-all text-sm font-medium ${language === lang
                                ? "border-white bg-gray-200 text-black"
                                : isDarkMode
                                  ? "border-[#404040] bg-transparent text-white hover:bg-[#353535]"
                                  : "border-gray-300 bg-transparent text-gray-700 hover:bg-gray-100"
                                }`}
                            >
                              {lang}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Voice Model Selector */}
                      <div className="space-y-4">
                        <div className={`flex items-center justify-between py-4 rounded-2xl bg-transparent`}>
                          <div className="flex items-center gap-3">
                            <Mic className={`h-5 w-5 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`} />
                            <span className={`text-sm font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Chat Voice Model</span>
                          </div>
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => setShowVoiceMenu(!showVoiceMenu)}
                              className={cn(
                                "flex items-center gap-2 px-3 py-1.5 h-auto rounded-xl transition-all border border-transparent hover:border-white/10",
                                isDarkMode ? "text-white/90 hover:bg-white/5" : "text-gray-900 hover:bg-gray-100"
                              )}
                            >
                              <span className="text-sm border-0">
                                {VOICES.find(v => v.id === selectedVoiceModel)?.title || "Aries"}
                              </span>
                              <ChevronDown className="h-4 w-4 text-gray-500" />
                            </button>

                            {showVoiceMenu && (
                              <>
                                <div
                                  className="inset-0 z-40"
                                  onClick={() => setShowVoiceMenu(false)}
                                />
                                <div
                                  className={cn(
                                    "absolute right-0 my-2 w-48 rounded-2xl p-1.5 shadow-xl border z-50 animate-in fade-in zoom-in-95 space-y-1 duration-200",
                                    isDarkMode ? "bg-[#252525] border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"
                                  )}
                                >
                                  {VOICES.map((voice) => (
                                    <button
                                      key={voice.id}
                                      onClick={() => {
                                        handleVoiceChange(voice.id);
                                        setShowVoiceMenu(false);
                                      }}
                                      className={cn(
                                        "w-full flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition-colors outline-none",
                                        selectedVoiceModel === voice.id
                                          ? (isDarkMode ? "bg-white/10" : "bg-gray-100")
                                          : "hover:bg-white/5"
                                      )}
                                    >
                                      <div className="flex flex-col items-start">
                                        <span className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                                          {voice.title}
                                        </span>
                                        <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                                          {voice.subtitle}
                                        </span>
                                      </div>
                                      {selectedVoiceModel === voice.id && <Check className={`h-4 w-4 ${isDarkMode ? "text-white" : "text-gray-900"}`} />}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {(hasVoiceSavedMessages || hasVoiceSavedAudio) && (
                          <div className="flex flex-col sm:flex-row gap-2">
                            {hasVoiceSavedMessages && (
                              <Button
                                type="button"
                                variant="secondary"
                                onClick={() => {
                                  try {
                                    sessionStorage.removeItem(VOICE_MODE_CARDS_SESSION_KEY);
                                    sessionStorage.removeItem("voice_mode_audio_v1");
                                  } catch {
                                  }
                                  refreshVoiceSessionFlags();
                                  toast({
                                    title: "",
                                    description: "Deleted saved voice messages",
                                  });
                                }}
                                className={cn(
                                  "rounded-full border transition-colors px-4 justify-center",
                                  isDarkMode
                                    ? "border-[#404040] bg-transparent text-white hover:bg-[#353535]"
                                    : "border-gray-300 bg-transparent text-gray-700 hover:bg-gray-100"
                                )}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete voice messages
                              </Button>
                            )}

                            {hasVoiceSavedAudio && (
                              <Button
                                type="button"
                                variant="secondary"
                                onClick={() => {
                                  try {
                                    const raw = sessionStorage.getItem(VOICE_MODE_CARDS_SESSION_KEY);
                                    if (raw) {
                                      const parsed = JSON.parse(raw);
                                      if (Array.isArray(parsed)) {
                                        const next = parsed.map((c: any) => ({ ...c, aiAudioBlob: null }));
                                        sessionStorage.setItem(VOICE_MODE_CARDS_SESSION_KEY, JSON.stringify(next));
                                      }
                                    }
                                    sessionStorage.removeItem("voice_mode_audio_v1");
                                  } catch {
                                  }
                                  refreshVoiceSessionFlags();
                                  toast({
                                    title: "",
                                    description: "Deleted saved voice audio",
                                  });
                                }}
                                className={cn(
                                  "rounded-full border transition-colors px-4 justify-center",
                                  isDarkMode
                                    ? "border-[#404040] bg-transparent text-white hover:bg-[#353535]"
                                    : "border-gray-300 bg-transparent text-gray-700 hover:bg-gray-100"
                                )}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete voice audio
                              </Button>
                            )}
                          </div>
                        )}
                      </div>

                    </div>
                  )}

                  {activeTab === "personalization" && (
                    <div className="animate-in px-3 fade-in slide-in-from-bottom-2 pb-24 lg:pb-0">
                      <div className={`py-4 flex items-center justify-between`}>
                        <div className="space-y-1">
                          <h2 className={`text-base font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>Personalization</h2>
                          <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>Add custom memory for the assistants.</p>
                        </div>
                      </div>

                      <div className={`${isDarkMode ? "bg-[#161616]" : "bg-gray-50"} mt-4 rounded-[32px] border ${isDarkMode ? "border-white/5" : "border-gray-100"} overflow-hidden`}>
                        <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? "border-white/5" : "border-gray-100"}`}>
                          <div className="space-y-1">
                            <p className={`text-base font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Key facts</p>
                          </div>
                          <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{memoryItems.length} / {MAX_MEMORY_ITEMS}</p>
                        </div>

                        <div className={`p-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                          <div className="space-y-4">

                            <DndContext
                              sensors={memorySensors}
                              collisionDetection={closestCenter}
                              onDragEnd={handleMemoryDragEnd}
                            >
                              <SortableContext
                                items={memoryItems}
                                strategy={verticalListSortingStrategy}
                              >
                                {memoryItems.map((item) => (
                                  <SortableMemoryItem
                                    key={item.id}
                                    id={item.id}
                                    value={item.text}
                                    onChange={updateMemoryItemText}
                                    onRemove={removeMemoryItem}
                                    isDarkMode={isDarkMode}
                                  />
                                ))}
                              </SortableContext>
                            </DndContext>

                            <div className="flex">
                              <button
                                type="button"
                                onClick={addMemoryItem}
                                disabled={memoryItems.length >= MAX_MEMORY_ITEMS}
                                className={cn(
                                  "mx-auto inline-flex size-10 items-center justify-center rounded-full border border-dashed bg-transparent transition-colors disabled:opacity-50",
                                  isDarkMode
                                    ? "border-zinc-700 text-zinc-400 hover:bg-zinc-900 hover:text-white"
                                    : "border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                                )}
                                aria-label="Add"
                              >
                                <Plus size={20} />
                              </button>
                            </div>

                            <div className="pt-2">
                              <Button
                                type="button"
                                onClick={handleSaveMemory}
                                disabled={isSavingMemory}
                                className={cn(
                                  "rounded-full w-full",
                                  isDarkMode ? "bg-white text-black hover:bg-zinc-200" : "bg-black text-white hover:bg-black/90"
                                )}
                              >
                                {isSavingMemory ? "Saving..." : "Update memory"}
                              </Button>
                            </div>

                            {/* Key Facts Summary Section */}
                            <div className="pt-6 border-t border-zinc-200 dark:border-zinc-800">

                              <div className={cn(
                                "w-full rounded-2xl border px-3 py-2 text-sm min-h-[80px]",
                                isDarkMode
                                  ? "bg-zinc-900 border-zinc-800 text-zinc-300"
                                  : "bg-zinc-50 border-gray-200 text-gray-700"
                              )}
                              >
                                {userFactsSummary ? (
                                  <p className="whitespace-pre-wrap leading-relaxed">{userFactsSummary}</p>
                                ) : (
                                  <p className="text-muted-foreground italic">No summary generated yet.</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "profile" && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 pb-24 lg:pb-10">
                      {/* Centered Profile Header */}
                      <div className="flex flex-col items-center gap-4 py-4">
                        <div className="relative group">
                          <div className={`h-24 w-24 rounded-full overflow-hidden flex items-center justify-center border-2 ${isDarkMode
                            ? "border-[#404040] bg-transparent hover:bg-[#353535]"
                            : "border-gray-300 bg-transparent hover:bg-gray-100"
                            }`}>
                            {avatarUrl ? (
                              <img
                                src={avatarUrl}
                                alt="Avatar"
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <User className="h-10 w-10 text-gray-500" />
                            )}
                          </div>
                          <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file || !authUser) return;

                              const reader = new FileReader();
                              reader.onload = () => {
                                setCropImage(reader.result as string);
                                setShowCropModal(true);
                              };
                              reader.readAsDataURL(file);
                              // Reset the input so the same file can be selected again
                              e.target.value = '';
                            }}
                          />
                          <Button
                            size="icon"
                            onClick={() => fileInputRef.current?.click()}
                            className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-[#303030] border border-white/10 hover:bg-[#353535] text-white"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                        <h2 className={`text-xl font-semibold ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>{name || "User"}</h2>

                      </div>

                      {/* Account Details List */}
                      <div className={`${isDarkMode ? "bg-[#161616]" : "bg-gray-50"} rounded-[32px] border ${isDarkMode ? "border-white/5" : "border-gray-100"} overflow-hidden`}>
                        {/* Name Row */}
                        <div className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-white/5" : "border-gray-100"}`}>
                          <div className="space-y-1">
                            <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Full name</p>
                            <p className={`text-base font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>{name || "-"}</p>
                          </div>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setIsEditingName(true)}
                            className={`rounded-full border transition-colors px-4 ${isDarkMode
                              ? "border-[#404040] bg-transparent text-white hover:bg-[#353535]"
                              : "border-gray-300 bg-transparent text-gray-700 hover:bg-gray-100"
                              }`}
                          >
                            Edit name
                          </Button>
                        </div>

                        {/* Email Row */}
                        <div className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-white/5" : "border-gray-100"}`}>
                          <div className="space-y-1">
                            <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Email</p>
                            <p className={`text-base font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>{authUser?.email || "-"}</p>
                          </div>
                        </div>

                        {/* Subscription Row */}
                        <div className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-white/5" : "border-gray-100"}`}>
                          <div className="space-y-1">
                            <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Subscription</p>
                            <p className={`text-base font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Manage your subscription</p>
                          </div>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setShowPricing(true)}
                            className={`rounded-full border transition-colors px-4 flex items-center gap-1 ${isDarkMode
                              ? "border-[#404040] bg-transparent text-white hover:bg-[#353535]"
                              : "border-gray-300 bg-transparent text-gray-700 hover:bg-gray-100"
                              }`}
                          >
                            Manage
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                        </div>

                        {/* Created At Row */}
                        <div className="p-6 flex justify-between">
                          <div className="space-y-1">
                            <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Account created</p>
                            <p className={`text-base font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>
                              {(profileData?.created_at || authUser?.created_at)
                                ? new Date(profileData?.created_at || authUser?.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : "-"}
                            </p>
                          </div>
                          {/* Destructive Actions */}
                          <div className="space-y-4">
                            <button
                              type="button"
                              onClick={() => setShowLogoutConfirm(true)}
                              className={`w-[130px] rounded-3xl transition-colors p-3 pl-4 h-auto flex items-center gap-3 border ${isDarkMode
                                ? "border-red-500/30 text-red-500 hover:text-red-400 hover:bg-red-500/10 bg-transparent"
                                : "border-red-200 text-red-600 hover:text-red-700 hover:bg-red-50 bg-white"
                                }`}
                            >
                              <LogOut className="h-5 w-5" />
                              <div className="text-left">
                                <p className="font-medium text-sm">Log out</p>
                              </div>
                            </button>
                            <Button
                              variant="outline"
                              className={`w-[230px] hidden rounded-3xl transition-colors py-4 h-auto justify-start px-6 border ${isDarkMode
                                ? "border-red-500/30 text-red-500 hover:text-red-400 hover:bg-red-500/10 bg-transparent"
                                : "border-red-200 text-red-600 hover:text-red-700 hover:bg-red-50 bg-white"
                                }`}
                            >
                              <div className="text-left">
                                <p className="font-medium">Delete account</p>
                                <p className="text-xs text-red-500/60 mt-0.5">Permanently remove all your data</p>
                              </div>
                            </Button>
                          </div>
                        </div>
                      </div>



                      {/* Custom Dialogs for Editing */}
                      <Dialog open={isEditingName} onOpenChange={setIsEditingName}>
                        <DialogContent className={`${isDarkMode ? "bg-[#1a1a1a] border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"} rounded-3xl p-6`}>
                          <DialogHeader>
                            <DialogTitle className={isDarkMode ? "text-white" : "text-gray-900"}>Update Name</DialogTitle>
                            <DialogDescription className={isDarkMode ? "text-gray-400" : "text-gray-500"}>Enter your new full name below.</DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <Input
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              className={`${isDarkMode ? "bg-[#2a2a2a] border-white/10 text-white" : "bg-gray-50 border-gray-200 text-gray-900"} rounded-2xl h-12 px-5 focus:ring-1 focus:ring-white/20`}
                            />
                            <div className="flex justify-end gap-3 pt-2">
                              <Button
                                variant="ghost"
                                onClick={() => setIsEditingName(false)}
                                className={`rounded-full px-6 ${isDarkMode ? "hover:bg-white/5" : "hover:bg-gray-100"}`}
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={async () => {
                                  setIsSaving(true);
                                  try {
                                    const { error, data } = await supabase
                                      .from("user_profiles")
                                      .upsert({
                                        user_id: authUser?.id,
                                        full_name: name,
                                        updated_at: new Date().toISOString()
                                      }, { onConflict: 'user_id' })
                                      .select()
                                      .single();
                                    if (error) throw error;
                                    if (data) setProfileData(data);
                                    toast({ title: "Name updated" });
                                    setIsEditingName(false);
                                  } catch (err: any) {
                                    toast({ title: "Error", description: err.message, variant: "destructive" });
                                  } finally {
                                    setIsSaving(false);
                                  }
                                }}
                                className={`rounded-full px-6 ${isDarkMode ? "bg-white text-black hover:bg-white/90" : "bg-black text-white hover:bg-black/90"}`}
                              >
                                {isSaving ? "Saving..." : "Save changes"}
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>

                      <Dialog open={isUpdatingEmail} onOpenChange={setIsUpdatingEmail}>
                        <DialogContent className={`${isDarkMode ? "bg-[#1a1a1a] border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"} rounded-3xl p-6`}>
                          <DialogHeader>
                            <DialogTitle className={isDarkMode ? "text-white" : "text-gray-900"}>Update Email</DialogTitle>
                            <DialogDescription className={isDarkMode ? "text-gray-400" : "text-gray-500"}>Enter your new email address below.</DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <Input
                              defaultValue={authUser?.email || ""}
                              className={`${isDarkMode ? "bg-[#2a2a2a] border-white/10 text-white" : "bg-gray-50 border-gray-200 text-gray-900"} rounded-2xl h-12 px-5 focus:ring-1 focus:ring-white/20`}
                              readOnly
                            />
                            <p className={`text-[12px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Note: Email changes require verification via your current address.</p>
                            <div className="flex justify-end gap-3 pt-2">
                              <Button
                                variant="ghost"
                                onClick={() => setIsUpdatingEmail(false)}
                                className={`rounded-full px-6 ${isDarkMode ? "hover:bg-white/5" : "hover:bg-gray-100"}`}
                              >
                                Cancel
                              </Button>
                              <Button
                                disabled
                                className={`rounded-full px-6 ${isDarkMode ? "bg-white text-black hover:bg-white/90" : "bg-black text-white hover:bg-black/90"}`}
                              >
                                Continue
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}

                  {activeTab === "data" && (
                    <div className="animate-in px-3 fade-in slide-in-from-bottom-2 pb-24 lg:pb-0">
                      <div className={`py-4 border-b ${isDarkMode ? "border-white/5" : "border-gray-100"} flex items-center justify-between`}>
                        <div className="space-y-1">
                          <h3 className={`text-sm font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Enhance the model for all users</h3>
                          <p className={`text-[12px] leading-relaxed pr-10 ${isDarkMode ? "text-white/40" : "text-gray-500"}`}>
                            Allow your content to train our models and improve services. We protect your data privacy.
                          </p>
                        </div>
                        <Switch
                          checked={improveModel}
                          onCheckedChange={(val) => {
                            setImproveModel(val);
                            localStorage.setItem("improveModel", val.toString());
                          }}
                        />
                      </div>

                      <div className={`py-4 border-b ${isDarkMode ? "border-white/5" : "border-gray-100"} flex items-center justify-between`}>
                        <span className={`text-sm font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Shared links</span>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setShowSharedLinks(true)}
                          className={`rounded-full border transition-colors px-4 ${isDarkMode
                            ? "border-[#404040] bg-transparent text-white hover:bg-[#353535]"
                            : "border-gray-300 bg-transparent text-gray-700 hover:bg-gray-100"
                            }`}
                        >
                          Manage
                        </Button>
                      </div>

                      <div className={`${isDarkMode ? "bg-[#161616]" : "bg-gray-50"} mt-4 rounded-[32px] border ${isDarkMode ? "border-white/5" : "border-gray-100"} overflow-hidden`}>
                        <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? "border-white/5" : "border-gray-100"}`}>
                          <div className="space-y-1">
                            <p className={`text-base font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Track your credits</p>
                          </div>
                          {/* <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate('/upgrade')}
                            className={`rounded-full border transition-colors px-4 ${isDarkMode
                              ? "border-[#404040] bg-transparent text-white hover:bg-[#353535]"
                              : "border-gray-300 bg-transparent text-gray-700 hover:bg-gray-100"
                              }`}
                          >
                            Upgrade
                          </Button> */}
                        </div>

                        <div className={`flex items-start justify-between px-4 py-6 border-b ${isDarkMode ? "border-white/5" : "border-gray-100"}`}>
                          <div className="space-y-1">
                            <p className={`text-base font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Remaining credits</p>
                          </div>
                          <p className={`text-base font-medium tabular-nums ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>{creditsRemaining}</p>
                        </div>

                        <div className="px-4 py-6">
                          <div className="space-y-1">
                            <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Daily credits</p>
                            <div className="flex items-center justify-between gap-4">
                              <p className={`text-base font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Refresh Daily</p>
                              <p className={`text-base font-medium tabular-nums ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>{dailyCreditsRemaining}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* <div className={`py-4 border-b ${isDarkMode ? "border-white/5" : "border-gray-100"} flex items-center justify-between`}>
                        <span className={`text-sm font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Export data</span>
                        <Button
                          variant="secondary"
                          size="sm"
                          className={`rounded-full border transition-colors px-4 ${isDarkMode
                            ? "border-[#404040] bg-transparent text-white hover:bg-[#353535]"
                            : "border-gray-300 bg-transparent text-gray-700 hover:bg-gray-100"
                            }`}
                        >
                          Export
                        </Button>
                      </div> */}

                      {/* <div className="py-4 flex items-center justify-between">
                        <span className={`text-sm font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Delete all chats</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowDeleteConfirm(true)}
                          className="rounded-full border border-red-500/50 bg-transparent text-red-500 hover:text-red-500 hover:bg-red-500/10 transition-colors px-4"
                        >
                          Delete all
                        </Button>
                      </div> */}
                    </div>
                  )}

                  {activeTab === "about" && (
                    <div className="space-y-0 animate-in fade-in slide-in-from-bottom-2 px-3 pb-24 lg:pb-0">
                      <div className={`py-4 border-b ${isDarkMode ? "border-white/5" : "border-gray-100"} flex items-center justify-between`}>
                        <span className={`text-sm font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Terms of Use</span>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate("/terms")}
                          className={`rounded-full border transition-colors px-4 ${isDarkMode
                            ? "border-[#404040] bg-transparent text-white hover:bg-[#353535]"
                            : "border-gray-300 bg-transparent text-gray-700 hover:bg-gray-100"
                            }`}
                        >
                          View
                        </Button>
                      </div>
                      <div className={`py-4 border-b ${isDarkMode ? "border-white/5" : "border-gray-100"} flex items-center justify-between`}>
                        <span className={`text-sm font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Privacy Policy</span>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate("/privacy")}
                          className={`rounded-full border transition-colors px-4 ${isDarkMode
                            ? "border-[#404040] bg-transparent text-white hover:bg-[#353535]"
                            : "border-gray-300 bg-transparent text-gray-700 hover:bg-gray-100"
                            }`}
                        >
                          View
                        </Button>
                      </div>
                      <div className={`py-4 border-b ${isDarkMode ? "border-white/5" : "border-gray-100"} flex items-center justify-between`}>
                        <span className={`text-sm font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Report an issue</span>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setShowReportModal(true)}
                          className={`rounded-full border transition-colors px-4 ${isDarkMode
                            ? "border-[#404040] bg-transparent text-white hover:bg-[#353535]"
                            : "border-gray-300 bg-transparent text-gray-700 hover:bg-gray-100"
                            }`}
                        >
                          Report
                        </Button>
                      </div>
                      <div className={`py-4 border-b ${isDarkMode ? "border-white/5" : "border-gray-100"} flex items-center justify-between`}>
                        <span className={`text-sm font-medium ${isDarkMode ? "text-white/90" : "text-gray-900"}`}>Feedback</span>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setShowFeedbackModal(true)}
                          className={`rounded-full border transition-colors px-4 ${isDarkMode
                            ? "border-[#404040] bg-transparent text-white hover:bg-[#353535]"
                            : "border-gray-300 bg-transparent text-gray-700 hover:bg-gray-100"
                            }`}
                        >
                          Submit
                        </Button>
                      </div>
                    </div>
                  )}

                  {activeTab === "apps" && (
                    <div className="space-y-6 pt-2 px-3 animate-in fade-in slide-in-from-bottom-2 h-[max-content] pb-32 lg:pb-0">
                      <div className={cn(
                        "rounded-3xl border p-5",
                        isDarkMode ? "border-white/10 bg-[#1c1c1c]/60" : "border-gray-200 bg-white/70"
                      )}>
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className={cn("text-base font-semibold", isDarkMode ? "text-white" : "text-gray-900")}>
                              Publish to explore
                            </div>
                            <div className={cn("mt-2 text-sm leading-relaxed", isDarkMode ? "text-white/60" : "text-gray-600")}>
                              Images and videos you create in Chitra AI(Not including chat images) can be seen by others in the explore feeds. Turning off this setting does not unpublish images and videos already in the feed.
                            </div>
                          </div>
                          <Switch checked={true} disabled className="opacity-100" />
                        </div>
                      </div>

                      <div className={cn(
                        "rounded-3xl border p-0 overflow-hidden",
                        isDarkMode ? "bg-[#1e1e1e] border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"
                      )}>
                        <div className={cn(
                          "flex items-center justify-between px-4 py-3 border-b",
                          isDarkMode ? "border-white/5" : "border-gray-100"
                        )}>
                          <input
                            value={appsChatSearch}
                            onChange={(e) => setAppsChatSearch(e.target.value)}
                            placeholder="Search chats..."
                            className={cn(
                              "w-full bg-transparent outline-none text-sm",
                              isDarkMode ? "text-white placeholder:text-white/40" : "text-gray-900 placeholder:text-gray-500"
                            )}
                          />
                        </div>

                        <div className="max-h-[360px] overflow-y-auto px-2 py-2 space-y-4">

                          {groupedAppsSessions.length === 0 ? (
                            <div className={cn(
                              "text-sm text-center py-10 px-4",
                              isDarkMode ? "text-gray-500" : "text-gray-500"
                            )}>
                              {appsChatSearch ? "No chats found" : "No chat history yet"}
                            </div>
                          ) : (
                            groupedAppsSessions.map((group) => (
                              <div key={group.label} className="space-y-1">
                                <div className={cn(
                                  "px-3 text-[11px] font-medium uppercase tracking-wide",
                                  isDarkMode ? "text-gray-500" : "text-gray-500"
                                )}>
                                  {group.label}
                                </div>
                                {group.items.map((s: any) => {
                                  const title = s.title || s.name || "Untitled Chat";
                                  const totalMessages = authUser ? s.message_count : (s.messageCount || s.messages?.length || 0);
                                  const lastDateRaw = (s.updated_at || s.updatedAt || s.timestamp || s.created_at) as string;
                                  const last = new Date(lastDateRaw);
                                  const lastLabel = last.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

                                  return (
                                    <div
                                      key={s.id || s.chatId}
                                      className={cn(
                                        "mx-1 px-3 py-2.5 rounded-2xl flex items-center justify-between gap-3 cursor-pointer",
                                        isDarkMode ? "hover:bg-white/5" : "hover:bg-gray-50"
                                      )}
                                      onClick={() => {
                                        const chatId = s.id || s.chatId;
                                        if (chatId) {
                                          navigate(`/c/${chatId}`);
                                        }
                                      }}
                                    >
                                      <div className="flex flex-col gap-0.5 min-w-0">
                                        <div
                                          className={cn(
                                            "text-sm underline-offset-[4px] h-[22px] font-medium truncate hover:underline text-left transition-all",
                                            isDarkMode ? "text-white/90" : "text-gray-900"
                                          )}
                                        >
                                          {title}
                                        </div>
                                        <div className={cn("text-xs flex items-center gap-2", isDarkMode ? "text-gray-400" : "text-gray-600")}>
                                          <span>{totalMessages} messages</span>
                                          <span className="opacity-60">•</span>
                                          <span className="truncate">Last active {lastLabel}</span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden fixed bottom-4 left-0 right-0 z-30 flex justify-center px-6">
        <div className={cn(
          "rounded-3xl border transition-all backdrop-blur-md p-2 shadow-2xl w-[max-content] max-w-[90vw]",
          isDarkMode ? "border-white/10 bg-[#1a1a1a]/80 text-white" : "border-gray-200 bg-white/80 text-gray-900"
        )}>
          <div className="flex rounded-3xl items-center gap-1 overflow-x-auto whitespace-nowrap no-scrollbar">
            {[{
              id: "general", label: "General", icon: SettingsIcon
            },
            { id: "profile", label: "Profile", icon: User },
            { id: "data", label: "Data", icon: Database },
            { id: "personalization", label: "Personalization", icon: Sparkles },
            { id: "apps", label: "Apps", icon: LayoutGrid },
            { id: "about", label: "About", icon: FileText },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => handleTabChange(id as SettingsTab)}
                className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-2xl text-xs font-medium transition-all duration-200 ${activeTab === id
                  ? isDarkMode ? "bg-white text-black" : "bg-black text-white"
                  : isDarkMode ? "text-gray-400 hover:bg-white/5 hover:text-white" : "text-gray-500 hover:bg-gray-100"
                  }`}
              >
                <Icon className="h-4 w-4" />
                {activeTab === id && <span>{label}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Modals are now handled by components at the bottom of the file */}

      {
        showDeleteConfirm && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowDeleteConfirm(false)}
            />
            <div
              className={`relative z-[81] w-[92%] max-w-md rounded-3xl border p-6 shadow-2xl ${isDarkMode
                ? "bg-[#1c1c1c] border-[#2a2a2a] text-white"
                : "bg-white border-gray-200 text-gray-900"
                }`}
            >
              <h3 className="text-lg font-semibold mb-2">Delete Chat History?</h3>
              <p className={`${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-6`}>
                This will permanently delete your chat history. This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  className={`px-6 py-2 rounded-full font-medium transition-colors ${isDarkMode
                    ? "bg-transparent hover:bg-white/5 text-gray-300"
                    : "bg-transparent hover:bg-gray-100 text-gray-700"
                    }`}
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-6 py-2 rounded-full bg-red-600 hover:bg-red-700 text-white font-medium transition-colors focus:ring-2 focus:ring-red-500/50"
                  onClick={handleClearChats}
                >
                  Yes, delete all
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Shared Links Custom Modal */}
      {showSharedLinks && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-2 sm:px-4 py-4">
          {/* Backdrop */}
          <div
            className={cn(
              "absolute inset-0 backdrop-blur-sm animate-in fade-in duration-300",
              isDarkMode ? "bg-black/60" : "bg-black/40"
            )}
            onClick={() => setShowSharedLinks(false)}
          />

          {/* Content */}
          <div className={cn(
            "relative w-full max-w-2xl rounded-[32px] overflow-hidden h-[500px] flex flex-col shadow-2xl animate-in zoom-in-95 duration-200 border",
            isDarkMode ? "bg-[#1e1e1e] border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"
          )}>
            {/* Header */}
            <div className={cn(
              "flex items-center justify-between p-4 border-b",
              isDarkMode ? "border-white/5" : "border-gray-100"
            )}>
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-medium ml-3">Shared links</h2>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSharedLinks(false)}
                className={cn(
                  "h-8 rounded-2xl w-8",
                  isDarkMode ? "text-gray-400 hover:text-white hover:bg-white/5" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                )}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* List Content */}
            <div className="flex-1 overflow-y-auto px-4 no-scrollbar">
              {isLoadingSharedLinks ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4">
                  <div className={cn(
                    "h-8 w-8 border-2 rounded-full animate-spin",
                    isDarkMode ? "border-white/10 border-t-white" : "border-black/10 border-t-black"
                  )} />
                  <p className={cn("text-sm", isDarkMode ? "text-gray-500" : "text-gray-600")}>Loading your shared links...</p>
                </div>
              ) : sharedLinks.length === 0 ? (
                <div className="text-center py-20">
                  <div className={cn(
                    "h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4",
                    isDarkMode ? "bg-[#1a1a1a]" : "bg-gray-100"
                  )}>
                    <LinkIcon className={cn("h-8 w-8", isDarkMode ? "text-gray-600" : "text-gray-500")} />
                  </div>
                  <h3 className={cn("text-sm font-medium", isDarkMode ? "text-white/90" : "text-gray-900")}>No shared links</h3>
                  <p className={cn("text-xs mt-1", isDarkMode ? "text-gray-500" : "text-gray-600")}>Links you share from chats will appear here.</p>
                </div>
              ) : (
                <div className="space-y-0">
                  {sharedLinks.map((link) => (
                    <div
                      key={link.id}
                      className={cn(
                        "flex items-center justify-between py-3 sm:py-4 border-b last:border-0 group transition-colors",
                        isDarkMode ? "border-white/5" : "border-gray-100"
                      )}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="shrink-0">
                          <LinkIcon className={cn("h-4 w-4", isDarkMode ? "text-gray-400" : "text-gray-500")} />
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 flex-1 min-w-0 py-0.5">
                          <button
                            onClick={() => {
                              window.open(`/c/${link.id}`, '_blank');
                            }}
                            className={cn(
                              "text-sm underline-offset-[4px] h-[22px] font-medium truncate hover:underline text-left transition-all max-w-full sm:max-w-[70%]",
                              isDarkMode ? "text-white/90" : "text-gray-900"
                            )}
                          >
                            {link.title || "Untitled Chat"}
                          </button>
                          <span className={cn("text-[11px] sm:text-xs whitespace-nowrap", isDarkMode ? "text-gray-500" : "text-gray-600")}>
                            Shared on {new Date(link.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="relative group/copy">
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                              "h-9 w-9 rounded-xl transition-all",
                              isDarkMode ? "text-gray-400 hover:text-white hover:bg-[#353535]" : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                            )}
                            onClick={() => {
                              const url = `${window.location.origin}/share/${link.id}`;
                              navigator.clipboard.writeText(url);
                              toast({ title: "Link copied to clipboard" });
                            }}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <div className={cn(
                            "absolute left-1/2 -translate-x-1/2 bottom-[110%] mb-2 px-2 py-1 text-[10px] rounded-md whitespace-nowrap opacity-0 group-hover/copy:opacity-100 transition-opacity duration-200 pointer-events-none z-50 border shadow-xl",
                            isDarkMode ? "bg-black/95 text-white border-white/10" : "bg-white text-gray-900 border-gray-200"
                          )}>
                            Copy link
                          </div>
                        </div>

                        <div className="relative group/delete">
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                              "h-9 w-9 rounded-xl transition-all",
                              isDarkMode ? "text-gray-400 hover:text-red-500 hover:bg-[#353535]" : "text-gray-600 hover:text-red-600 hover:bg-gray-100"
                            )}
                            onClick={() => handleDeleteSharedLink(link.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <div className={cn(
                            "absolute left-1/2 -translate-x-1/2 bottom-[110%] mb-2 px-2 py-1 text-[10px] rounded-md whitespace-nowrap opacity-0 group-hover/delete:opacity-100 transition-opacity duration-200 pointer-events-none z-50 border shadow-xl",
                            isDarkMode ? "bg-black/95 text-white border-white/10" : "bg-white text-gray-900 border-gray-200"
                          )}>
                            Delete
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {
        showLogoutConfirm && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setShowLogoutConfirm(false)}
            />
            <div
              className={`relative z-[81] w-[92%] max-w-md rounded-2xl border p-5 shadow-2xl ${isDarkMode
                ? "bg-[#1c1c1c] border-[#2a2a2a] text-white"
                : "bg-white border-gray-200 text-gray-900"
                }`}
            >
              <h3 className="text-lg font-semibold mb-2">Log out</h3>
              <p
                className={`${isDarkMode ? "text-gray-300" : "text-gray-700"
                  } mb-4`}
              >
                You will be logged out from this device.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  className={`px-3 py-2 rounded-full ${isDarkMode
                    ? "bg-transparent hover:bg-[#2a2a2a] text-gray-300"
                    : "bg-transparent hover:bg-gray-100 text-gray-700"
                    }`}
                  onClick={() => setShowLogoutConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className={`px-4 py-2 rounded-full ${isDarkMode
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-red-600 hover:bg-red-700 text-white"
                    }`}
                  onClick={() => {
                    setShowLogoutConfirm(false);
                    handleLogout();
                  }}
                >
                  Yes, log out
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Image Crop Modal */}
      {
        showCropModal && cropImage && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center px-2 sm:px-4 py-4">
            <div
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setShowCropModal(false)}
            />
            <div className={`relative z-[111] w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl ${isDarkMode ? "bg-[#1c1c1c] border border-white/10" : "bg-white border border-gray-200"}`}>
              <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <h3 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>Crop Profile Picture</h3>
                <button onClick={() => setShowCropModal(false)} className="text-gray-400 hover:text-white transition-colors">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="relative h-[400px] w-full bg-black">
                <Cropper
                  image={cropImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={handleCropChange}
                  onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
                  onZoomChange={handleZoomChange}
                />
              </div>

              <div className="p-6 space-y-6">
                {/* Zoom Controls */}
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => setZoom(Math.max(zoom - 0.2, 1))}
                    disabled={zoom <= 1}
                    className={cn(
                      "flex items-center justify-center w-10 h-10 rounded-lg transition-all",
                      zoom <= 1
                        ? "bg-white/5 text-white/30 cursor-not-allowed"
                        : "bg-white/10 text-white hover:bg-white/20"
                    )}
                    title="Zoom Out"
                  >
                    <ZoomOut className="w-5 h-5" />
                  </button>

                  <button
                    onClick={() => {
                      setCrop({ x: 0, y: 0 });
                      setZoom(1);
                    }}
                    className="flex items-center justify-center w-10 h-10 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all"
                    title="Reset"
                  >
                    <RotateCcw className="w-5 h-5" />
                  </button>

                  <button
                    onClick={() => setZoom(Math.min(zoom + 0.2, 3))}
                    disabled={zoom >= 3}
                    className={cn(
                      "flex items-center justify-center w-10 h-10 rounded-lg transition-all",
                      zoom >= 3
                        ? "bg-white/5 text-white/30 cursor-not-allowed"
                        : "bg-white/10 text-white hover:bg-white/20"
                    )}
                    title="Zoom In"
                  >
                    <ZoomIn className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => setShowCropModal(false)}
                    className={`rounded-full px-6 ${isDarkMode ? "hover:bg-white/5 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!croppedAreaPixels || !cropImage || !authUser) return;
                      setIsSaving(true);
                      setShowCropModal(false);

                      try {
                        const croppedBlob = await getCroppedImg(cropImage, croppedAreaPixels);
                        if (!croppedBlob) throw new Error("Could not crop image");

                        const reader = new FileReader();
                        reader.onload = async () => {
                          const base64Data = reader.result as string;

                          // Cloudinary Upload Logic
                          const cloudName = "dcqtcd2zs";
                          const uploadPreset = "image_server_1";
                          const folder = "avatars";

                          const formData = new FormData();
                          formData.append("file", base64Data);
                          formData.append("upload_preset", uploadPreset);
                          formData.append("folder", folder);

                          const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
                            method: "POST",
                            body: formData,
                          });

                          if (!response.ok) throw new Error("Cloudinary upload failed");
                          const data = await response.json();

                          const publicUrl = data.secure_url.replace('/upload/', '/upload/q_80/');

                          const { error: updateError } = await supabase
                            .from("user_profiles")
                            .upsert({
                              user_id: authUser.id,
                              avatar_url: publicUrl,
                              updated_at: new Date().toISOString()
                            }, { onConflict: 'user_id' });

                          if (updateError) throw updateError;

                          setAvatarUrl(publicUrl);
                          setProfileData((prev: any) => prev ? { ...prev, avatar_url: publicUrl } : { avatar_url: publicUrl });
                          toast({ title: "Profile picture updated" });
                        };
                        reader.readAsDataURL(croppedBlob);
                      } catch (err: any) {
                        console.error("Scale/Upload error:", err);
                        toast({ title: "Update failed", description: err.message, variant: "destructive" });
                      } finally {
                        setIsSaving(false);
                      }
                    }}
                    className={`rounded-full px-8 ${isDarkMode ? "bg-white text-black hover:bg-white/90" : "bg-black text-white hover:bg-black/90"}`}
                  >
                    {isSaving ? "Saving..." : "Save Selection"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      }
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        userEmail={authUser?.email || ""}
      />

      <ReportBugModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        userEmail={authUser?.email || ""}
      />

      {/* Pricing Modal */}
      <PricingModal
        isOpen={showPricing}
        onClose={() => setShowPricing(false)}
        planTier={planTier}
      />
    </div >
  );
}

export default Settings;
